import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, RefreshCw, Undo2, UserCheck, UserPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errMsg = (e, fallback) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : fallback);

const Sources = ({ sources = [] }) => (
  <div className="text-xs text-muted-foreground">
    {sources.map((s, i) => (
      <span key={i} className="mr-3">
        {s.count} {s.kind}{s.agency ? ` · ${s.agency}` : ''}{s.last_week ? ` · week ending ${s.last_week}` : ''}
      </span>
    ))}
  </div>
);

const PendingRow = ({ item, people, onResolve, onNew, busy }) => {
  const [other, setOther] = useState('');
  const [filter, setFilter] = useState('');
  const shown = people.filter((p) => `${p.first_name} ${p.last_name}`.toLowerCase().includes(filter.trim().toLowerCase())).slice(0, 200);
  return (
    <Card className="p-4 space-y-3" data-testid={`name-review-item-${item.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-base">{item.uploaded_name}</div>
          <Sources sources={item.sources} />
        </div>
        <Badge variant="outline" className={item.candidates?.length ? 'border-amber-400 text-amber-700' : 'border-red-300 text-red-700'}>
          {item.candidates?.length ? 'Needs your review' : 'No match'}
        </Badge>
      </div>
      <div className="flex flex-wrap gap-2">
        {(item.candidates || []).map((c) => (
          <Button key={c.personnel_id} size="sm" variant="outline" disabled={busy} onClick={() => onResolve(item, c.personnel_id)}
            data-testid={`name-review-pick-${item.id}-${c.personnel_id}`}>
            <UserCheck size={14} className="mr-1" /> {c.name}
            <span className="ml-2 text-xs text-muted-foreground">{Math.round(c.score * 100)}% · {c.state || '—'} · {c.discipline || '—'}</span>
          </Button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input className="border rounded-md px-2 py-1.5 text-sm w-44" placeholder="Search people…" value={filter}
          onChange={(e) => setFilter(e.target.value)} data-testid={`name-review-other-search-${item.id}`} />
        <select className="border rounded-md px-2 py-1.5 text-sm min-w-[240px]" value={other} onChange={(e) => setOther(e.target.value)}
          data-testid={`name-review-other-select-${item.id}`}>
          <option value="">Pick someone else…</option>
          {shown.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.state ? ` (${p.state})` : ''}</option>)}
        </select>
        <Button size="sm" disabled={!other || busy} onClick={() => onResolve(item, other)} data-testid={`name-review-assign-other-${item.id}`}>Assign</Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => onNew(item)} data-testid={`name-review-mark-new-${item.id}`}>
          <UserPlus size={14} className="mr-1" /> New / not in personnel
        </Button>
      </div>
    </Card>
  );
};

const DecidedRow = ({ item, onUndo, busy }) => (
  <div className="flex items-center justify-between border-b py-2 text-sm" data-testid={`name-review-decided-${item.id}`}>
    <div>
      <span className="font-medium">{item.uploaded_name}</span>
      <span className="text-muted-foreground"> → {item.status === 'new' ? 'New / not in personnel' : `${item.resolved_name} (${item.resolved_state || '—'})`}</span>
      <span className="ml-2 text-xs text-muted-foreground">{item.status === 'auto' ? 'auto-matched' : `by ${item.decided_by || '—'}`}</span>
    </div>
    <Button size="sm" variant="ghost" disabled={busy} onClick={() => onUndo(item)} data-testid={`name-review-undo-${item.id}`}>
      <Undo2 size={14} className="mr-1" /> Undo
    </Button>
  </div>
);

export default function NameReview() {
  const [pending, setPending] = useState([]);
  const [decided, setDecided] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, d, ppl] = await Promise.all([
        axios.get(`${API}/name-review?status=pending`),
        axios.get(`${API}/name-review?status=decided`),
        axios.get(`${API}/personnel?status=Active`),
      ]);
      setPending(p.data || []); setDecided(d.data || []);
      setPeople((ppl.data || []).filter((x) => String(x.personnel_type || '').trim()).sort((a, b) => `${a.last_name}`.localeCompare(`${b.last_name}`)));
    } catch (e) {
      toast.error(errMsg(e, 'Could not load the review queue'));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); toast.success(ok(r?.data || {})); await load(); window.dispatchEvent(new Event('name-review-changed')); }
    catch (e) { toast.error(errMsg(e, 'Action failed')); }
    finally { setBusy(false); }
  };
  const onResolve = (item, pid) => act(() => axios.post(`${API}/name-review/${item.id}/resolve`, { personnel_id: pid }),
    (d) => `"${item.uploaded_name}" → ${d.resolved_name}. ${d.relinked_visits || 0} visit(s) re-attributed; spelling saved as alias.`);
  const onNew = (item) => act(() => axios.post(`${API}/name-review/${item.id}/mark-new`), () => `"${item.uploaded_name}" marked as new — won't be asked again.`);
  const onUndo = (item) => act(() => axios.post(`${API}/name-review/${item.id}/undo`), () => `Undid decision for "${item.uploaded_name}".`);
  const onScan = () => act(() => axios.post(`${API}/name-review/scan`), (d) => `Scan done: ${d.auto || 0} auto-matched, ${d.queued || 0} need review.`);

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="name-review-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Name review</h1>
          <p className="text-sm text-muted-foreground mt-1">Uploaded clinician names that couldn't be matched to one person with confidence. Your choice is remembered and saved as an alias.</p>
        </div>
        <Button variant="outline" onClick={onScan} disabled={busy} data-testid="name-review-scan-btn"><RefreshCw size={14} className="mr-1" /> Re-scan uploads</Button>
      </div>
      {loading ? <Loader2 className="animate-spin" /> : (
        <>
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Needs your review <Badge className="ml-2" data-testid="name-review-pending-count">{pending.length}</Badge></h2>
            {pending.length === 0 && <p className="text-sm text-muted-foreground" data-testid="name-review-empty">Nothing waiting — every uploaded name is matched.</p>}
            {pending.map((it) => <PendingRow key={it.id} item={it} people={people} onResolve={onResolve} onNew={onNew} busy={busy} />)}
          </section>
          <section>
            <h2 className="text-lg font-semibold mb-2">Decided ({decided.length})</h2>
            <Card className="px-4 py-2">
              {decided.length === 0 ? <p className="text-sm text-muted-foreground py-2">No decisions yet.</p>
                : decided.map((it) => <DecidedRow key={it.id} item={it} onUndo={onUndo} busy={busy} />)}
            </Card>
          </section>
        </>
      )}
    </div>
  );
}
